// ProjectReportButton — buyer-side "report inaccuracy" CTA + form
// (PROP-82). Mounted on the project detail surface. Click opens an
// inline form (no modal — the parent surface is already a modal /
// drawer in most contexts); category picker + optional note; submits
// to /api/projects/{slug}/report.
//
// Auth: requires a signed-in user. When the API returns 401 (the
// cookie is missing / expired) we surface the message verbatim so the
// FE caller / surrounding auth gate handles redirect — this component
// stays focused on the report flow.
//
// Rate-limit: 429 from the backend renders a friendly "you've reported
// a lot of projects recently; try again later" message.

import { useState } from "react";
import {
  submitProjectReport,
  PROJECT_REPORT_CATEGORIES,
  type ProjectReportCategory,
} from "@/lib/exploreApi";
import { ApiError } from "@/lib/api";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export function ProjectReportButton({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<ProjectReportCategory>("wrong_unit_mix");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // Empty slug → render nothing rather than send a request that will
  // 400. Offline mocks / pre-hydration paths can hit this branch.
  if (!slug) return null;

  const reset = () => {
    setOpen(false);
    setCategory("wrong_unit_mix");
    setNote("");
    setStatus({ kind: "idle" });
  };

  const submit = async () => {
    setStatus({ kind: "submitting" });
    try {
      await submitProjectReport(slug, { category, note: note.trim() || undefined });
      setStatus({ kind: "success" });
    } catch (e) {
      let message = "Couldn't submit your report. Please try again.";
      if (e instanceof ApiError) {
        if (e.status === 401) message = "Sign in to report a project.";
        else if (e.status === 429) message = "You've reported a lot of projects recently. Try again later.";
        else if (e.status === 400) message = "Please pick a category before submitting.";
      }
      setStatus({ kind: "error", message });
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          background: "transparent",
          border: "1px solid var(--rule)",
          padding: "6px 12px",
          borderRadius: 6,
          fontFamily: "var(--font-ui)",
          fontSize: 11.5,
          color: "var(--ink-3)",
          cursor: "pointer",
        }}
        title="Report inaccurate data on this project"
      >
        🚩 Report inaccuracy
      </button>
    );
  }

  if (status.kind === "success") {
    return (
      <div
        role="status"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--rule)",
          borderRadius: 8,
          padding: 12,
          fontFamily: "var(--font-ui)",
          fontSize: 12.5,
          color: "var(--ink-2)",
          maxWidth: 360,
        }}
      >
        <div style={{ fontWeight: 500, color: "var(--ink)", marginBottom: 4 }}>
          Thanks — we logged your report.
        </div>
        <div style={{ color: "var(--ink-3)" }}>
          Our data team reviews these regularly. You can close this card.
        </div>
        <button
          type="button"
          onClick={reset}
          style={{
            marginTop: 10,
            background: "transparent",
            border: "1px solid var(--rule)",
            padding: "5px 10px",
            borderRadius: 6,
            fontSize: 11.5,
            color: "var(--ink-3)",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--rule)",
        borderRadius: 8,
        padding: 14,
        maxWidth: 380,
      }}
    >
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>
        Report inaccuracy on this project
      </div>
      <label
        style={{
          display: "block",
          fontFamily: "var(--font-ui)",
          fontSize: 11.5,
          color: "var(--ink-3)",
          marginBottom: 4,
        }}
      >
        Category
      </label>
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as ProjectReportCategory)}
        disabled={status.kind === "submitting"}
        style={{
          width: "100%",
          padding: "7px 8px",
          borderRadius: 6,
          border: "1px solid var(--rule)",
          background: "var(--surface)",
          fontFamily: "var(--font-ui)",
          fontSize: 12.5,
          color: "var(--ink-2)",
          marginBottom: 10,
        }}
      >
        {PROJECT_REPORT_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
      <label
        style={{
          display: "block",
          fontFamily: "var(--font-ui)",
          fontSize: 11.5,
          color: "var(--ink-3)",
          marginBottom: 4,
        }}
      >
        Note (optional)
      </label>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={status.kind === "submitting"}
        rows={3}
        maxLength={1024}
        placeholder="What's wrong with the data?"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "7px 8px",
          borderRadius: 6,
          border: "1px solid var(--rule)",
          background: "var(--surface)",
          fontFamily: "var(--font-ui)",
          fontSize: 12.5,
          color: "var(--ink-2)",
          resize: "vertical",
          marginBottom: 10,
        }}
      />
      {status.kind === "error" && (
        <div
          role="alert"
          style={{
            background: "var(--neg-bg, #fee2e2)",
            color: "var(--neg, #991b1b)",
            border: "1px solid var(--neg-rule, #fecaca)",
            padding: "6px 10px",
            borderRadius: 6,
            fontFamily: "var(--font-ui)",
            fontSize: 11.5,
            marginBottom: 10,
          }}
        >
          {status.message}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={submit}
          disabled={status.kind === "submitting"}
          style={{
            background: "var(--accent)",
            color: "var(--bubble-me-ink, #fff)",
            border: "none",
            padding: "7px 14px",
            borderRadius: 6,
            fontFamily: "var(--font-ui)",
            fontSize: 12,
            fontWeight: 500,
            cursor: status.kind === "submitting" ? "wait" : "pointer",
            opacity: status.kind === "submitting" ? 0.6 : 1,
          }}
        >
          {status.kind === "submitting" ? "Sending…" : "Send report"}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={status.kind === "submitting"}
          style={{
            background: "transparent",
            border: "1px solid var(--rule)",
            padding: "7px 14px",
            borderRadius: 6,
            fontFamily: "var(--font-ui)",
            fontSize: 12,
            color: "var(--ink-3)",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
